import type React from 'react';
import ReactSelect, { type StylesConfig } from 'react-select';

import { Field, type FieldProps } from 'formik';

import { mergeSelectStyles, reactSelectMenuPortalProps } from './reactSelectTheme';

interface Option {
  value: string;
  label: string;
}

interface SelectFieldProps {
  name: string;
  label?: string;
  required?: boolean;
  options: Option[];
  placeholder?: string;
  isMulti?: boolean;
  isDisabled?: boolean;
  /** Formik field is the source of truth; accepted for call-site compatibility. */
  value?: string;
  /** Single-select: called instead of the default setFieldValue (caller updates Formik). */
  onChange?: (value: string) => void;
}

export const SelectField: React.FC<SelectFieldProps> = ({
  name,
  label,
  required,
  options,
  placeholder = 'Выберите...',
  isMulti = false,
  isDisabled = false,
  onChange,
}) => {
  const customStyles: StylesConfig<Option, typeof isMulti> = mergeSelectStyles<Option, typeof isMulti>({
    control: (_base, state) => ({
      opacity: isDisabled ? 0.6 : 1,
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      borderColor: state.isFocused ? '#1E88E5' : 'transparent',
    }),
  });

  return (
    <Field name={name}>
      {({ field, form, meta }: FieldProps) => (
        <div className='form-field'>
          {label && (
            <label className='label'>
              <span className='labelText'>{label}</span>
              {required && <span className='requiredLabelMarker'>*</span>}
            </label>
          )}

          <ReactSelect
            options={options}
            name={field.name}
            value={options.find((option) => option.value === field.value) || null}
            onChange={(selectedOption) => {
              if (isDisabled) return;

              if (onChange && !isMulti) {
                onChange((selectedOption as Option | null)?.value || '');
                return;
              }

              form.setFieldValue(
                name,
                isMulti
                  ? (selectedOption as Option[]).map((opt) => opt.value)
                  : (selectedOption as Option)?.value || '',
              );
            }}
            onBlur={field.onBlur}
            placeholder={placeholder}
            isMulti={isMulti}
            styles={customStyles}
            isSearchable={false}
            isDisabled={isDisabled}
            {...reactSelectMenuPortalProps}
            components={{
              IndicatorSeparator: () => null,
            }}
          />

          {meta.touched && meta.error && (
            <div
              className='helperText highlight-error form-error-message'
              style={{
                color: '#ff0000',
                fontSize: '16px',
                marginTop: '5px',
              }}
            >
              {meta.error}
            </div>
          )}
        </div>
      )}
    </Field>
  );
};
